import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { useAuth } from '@/auth/AuthContext';
import { logActivity } from '@/data/activityRepo';
import { describeActivityPath, type ActivityDraftType } from '@/types/types_activity';

/**
 * How long the same route is treated as one visit.
 *
 * Without it a bounce between two pages, or React re-running the effect, would
 * count twice; with it, coming back to an experience an hour later still does
 * count — which is the behaviour we want, since returning to a page is the
 * signal we are looking for.
 */
const repeatViewWindowMs = 30_000;

/**
 * Returns a function that records an activity event, or does nothing.
 *
 * Nothing is recorded unless the caller is a signed-in partner: admins are
 * skipped so our own browsing does not appear in a partner's numbers, and there
 * is no useful event to write for a user with no partner at all.
 */
export function useLogActivity() {
  const { user, isAdmin, isPartner, partnerUser } = useAuth();
  const partnerId = partnerUser?.partnerId;
  const uid = user?.uid;
  const userEmail = user?.email ?? '';

  return useCallback(
    (draft: ActivityDraftType) => {
      if (!uid || !partnerId || !isPartner || isAdmin) return;
      void logActivity({ uid, userEmail, partnerId }, draft);
    },
    [uid, userEmail, partnerId, isPartner, isAdmin],
  );
}

/**
 * Records a view every time a partner lands on a new route.
 *
 * Mounted once, in `PublicLayout` — every page a partner can reach sits under
 * it, and putting it there rather than in each page means a new route is
 * tracked by existing. The admin CMS uses its own layout and so is never seen
 * here, which is deliberate.
 */
export function useTrackActivity() {
  const { pathname } = useLocation();
  const logEvent = useLogActivity();
  const lastView = useRef<{ path: string; at: number } | null>(null);

  useEffect(() => {
    const now = Date.now();
    const last = lastView.current;
    if (last && last.path === pathname && now - last.at < repeatViewWindowMs) return;
    lastView.current = { path: pathname, at: now };
    logEvent(describeActivityPath(pathname));
  }, [pathname, logEvent]);
}
